import { QueryOptions, WithRequired } from '@tanstack/react-query';

import {
  getCustomerFeedbacksForStore,
  getCustomerFeedbacksForUsecase,
  getFlowMessageOptimizerStats,
  getFlowMessagesForStoreKey,
  getFlowsForStoreKey,
  getKlaviyoAccount,
  getLatestExperimentPhaseForStoreKey,
  getMonthlyOptimzerMetrics,
  getSPOptimizerMessageExperimentShardMetrics,
  getSPOptimizerMessageExperimentsForSPOptimizerStore,
  getSPOptimizerMessageExperimentsForStore,
  getSPOptimizerStore,
  getSPOptimizerStoreAggregatedMetrics,
  getSPOptimizerStoreExcludePhrases,
  getSPOptimizerStoreIncludePhrases,
  getSPOptimizerStores,
  getSavedVideoScripts,
  getShopifyShop,
  getStoreAggregatedMetrics,
  getStoreDailyMetrics,
  getUsecase,
  getUsecasesForStore,
  postShopifyCartProductImages,
} from './methods';

const makeQuery = <F extends (...a: [...Parameters<F>]) => Promise<Awaited<ReturnType<F>>>>(
  apiMethod: F,
  otherOptions: (
    ...args: [...Parameters<F>]
  ) => Omit<WithRequired<QueryOptions<Awaited<ReturnType<F>>>, 'queryKey'>, 'queryFn'>,
): ((...args: [...Parameters<F>]) => WithRequired<QueryOptions<Awaited<ReturnType<F>>>, 'queryFn' | 'queryKey'>) => {
  return (...args) => ({
    queryFn: () => apiMethod(...args),
    ...otherOptions(...args),
  });
};

export const queries = {
  usecase: makeQuery(getUsecase, (usecaseId: number) => ({
    queryKey: ['usecase', usecaseId],
  })),
  usecasesForStore: makeQuery(getUsecasesForStore, (storeKey: string | undefined) => ({
    queryKey: ['usecases', storeKey],
  })),
  spOptimizerStores: makeQuery(getSPOptimizerStores, () => ({
    queryKey: ['spOptimizerStores'],
  })),
  spOptimizerStore: makeQuery(getSPOptimizerStore, (spOptimizerStoreID: number) => ({
    queryKey: ['spOptimizerStore', spOptimizerStoreID],
  })),
  spOptimizerStoreExcludePhrases: makeQuery(getSPOptimizerStoreExcludePhrases, (spOptimizerStoreID: number) => ({
    queryKey: ['spOptimizerStoreExcludePhrases', spOptimizerStoreID],
  })),
  spOptimizerStoreIncludePhrases: makeQuery(getSPOptimizerStoreIncludePhrases, (spOptimizerStoreID: number) => ({
    queryKey: ['spOptimizerStoreIncludePhrases', spOptimizerStoreID],
  })),
  spOptimizerMessageExperimentShardMetrics: makeQuery(getSPOptimizerMessageExperimentShardMetrics, () => ({
    queryKey: ['spOptimizerMessageExperimentShardMetrics'],
  })),
  storeAggregatedMetrics: makeQuery(
    getStoreAggregatedMetrics,
    (storeKey: string, days: number, useCaseId?: number) => ({
      queryKey: ['store', storeKey, 'usecase', useCaseId, 'aggregate-metrics', days],
    }),
  ),
  storeDailyMetrics: makeQuery(getStoreDailyMetrics, (storeKey: string, days: number, useCaseId?: number) => ({
    queryKey: ['store', storeKey, 'usecase', useCaseId, 'daily-metrics', days],
  })),
  storeMonthlyOptimzerMetrics: makeQuery(getMonthlyOptimzerMetrics, (storeKey: number | undefined) => ({
    queryKey: ['store', storeKey],
  })),
  customerFeedbacksForUsecase: makeQuery(
    getCustomerFeedbacksForUsecase,
    (usecaseId: number, page = 1, pageSize = 10) => ({
      queryKey: ['usecase', 'user-feedback', usecaseId, page, pageSize],
    }),
  ),
  customerFeedbacksForStore: makeQuery(
    getCustomerFeedbacksForStore,
    (storeId: number | undefined, page = 1, pageSize = 25) => ({
      queryKey: ['store', 'user-feedback', storeId, page, pageSize],
    }),
  ),
  klaviyoAccount: makeQuery(getKlaviyoAccount, (klaviyoAccountId: number) => ({
    queryKey: ['klaviyo-account', klaviyoAccountId],
  })),
  shopifyShop: makeQuery(getShopifyShop, (shopifyShopId: number) => ({
    queryKey: ['shopify-shop', shopifyShopId],
  })),
  shopifyCartProductImages: makeQuery(
    postShopifyCartProductImages,
    (
      storeKey: string,
      shopifyCheckoutId: string | undefined | null,
      shopifyCheckoutToken: string | undefined | null,
      shopifyProductIds: string[],
    ) => ({
      queryKey: [
        'store',
        storeKey,
        'shopify-cart-product-images',
        shopifyCheckoutId,
        shopifyCheckoutToken,
        shopifyProductIds,
      ],
    }),
  ),
  savedVideoScripts: makeQuery(getSavedVideoScripts, (useCaseId: number) => ({
    queryKey: ['usecase', useCaseId, 'get-saved-video-scripts'],
  })),
  spOptimizerMessageExperimentsForSPOptimizerStore: makeQuery(
    getSPOptimizerMessageExperimentsForSPOptimizerStore,
    (spOptimizerStoreId: number | undefined) => ({
      queryKey: ['sp-optimizer-message-experiments', 'for-sp-optimizer-store', spOptimizerStoreId],
    }),
  ),
  spOptimizerMessageExperimentsForStore: makeQuery(
    getSPOptimizerMessageExperimentsForStore,
    (storeKey: string | undefined) => ({
      queryKey: ['sp-optimizer-message-experiments', 'for-store', storeKey],
    }),
  ),
  spOptimizerStoreAggregatedMetrics: makeQuery(
    getSPOptimizerStoreAggregatedMetrics,
    (spOptimizerStoreId: number, days: number, flowId?: number, flowMessageId?: number) => ({
      queryKey: [
        'sp-optimizer-store',
        spOptimizerStoreId,
        'flow',
        flowId,
        'flow-message',
        flowMessageId,
        'aggregate-metrics',
        days,
      ],
    }),
  ),
  flowsForStoreKey: makeQuery(getFlowsForStoreKey, (storeKey: string) => ({
    queryKey: ['flows-for-store-key', storeKey],
  })),
  flowMessageForStoreKey: makeQuery(getFlowMessagesForStoreKey, (storeKey: string) => ({
    queryKey: ['flow-messages-for-store-key', storeKey],
  })),
  flowMessageOptimizerStats: makeQuery(getFlowMessageOptimizerStats, (storeKey: string) => ({
    queryKey: ['flow-messages-optimizer-stats-for-store-key', storeKey],
  })),
  latestExperimentPhaseForStoreKey: makeQuery(getLatestExperimentPhaseForStoreKey, (storeKey: string) => ({
    queryKey: ['latest-experiment-phases-for-store-key', storeKey],
  })),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
} as const satisfies Record<string, (...args: any[]) => WithRequired<QueryOptions<any>, 'queryKey' | 'queryFn'>>;
